import type { Task } from "@gw/shared-types";
import { actorRoleLabel } from "@/features/operations/operationsLabels";
import { deriveTaskStatus } from "./taskTotals";

/** The request's tasks split the way the Tasks fold shows them: finished, the one in hand, the rest behind it. */
export interface TaskGroups {
  /** Completed or discarded, in FBO order. */
  done: Task[];
  /** The task in hand: the sitting task when the attention rules name one, else the first open task in FBO order. */
  now: Task | null;
  /** Open tasks behind the one in hand, in FBO order; they run in sequence, so they are not waiting yet. */
  queued: Task[];
}

function isDone(task: Task): boolean {
  const status = deriveTaskStatus(task);
  return status === "Completed" || status === "Discarded";
}

export function groupTasks(tasks: Task[], sittingTaskId?: string): TaskGroups {
  const done = tasks.filter(isDone);
  const open = tasks.filter((t) => !isDone(t));
  const now = open.find((t) => t.taskId === sittingTaskId) ?? open[0] ?? null;
  const queued = now ? open.filter((t) => t.taskId !== now.taskId) : [];
  return { done, now, queued };
}

function roleOf(task: Task): string {
  const first = task.actions[0];
  return first ? actorRoleLabel(first.actorRole) : "Unassigned";
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** One line for the collapsed Queued row: who the queue belongs to and what it waits for. */
export function queuedSummary(queued: Task[], now: Task | null): string {
  if (queued.length === 0) return "";
  const actors = new Set(queued.map((t) => t.actions[0]?.actorName ?? ""));
  const after = now ? ` · start after ${now.taskName}` : "";
  const [only] = actors;
  const first = queued[0];
  if (actors.size === 1 && only && first) return `all ${roleOf(first)} tasks (${firstName(only)})${after}`;
  const counts = new Map<string, number>();
  for (const t of queued) counts.set(roleOf(t), (counts.get(roleOf(t)) ?? 0) + 1);
  const roles = [...counts.entries()]
    .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
    .map(([role, n]) => `${role} ×${n}`)
    .join(", ");
  return `${roles}${after}`;
}

/** The fold's sub-line: "5 done, 1 now, 12 queued" or "all done". */
export function taskGroupsSummary(groups: TaskGroups): string {
  if (!groups.now) return "all done";
  return `${groups.done.length} done, 1 now, ${groups.queued.length} queued`;
}
